/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-12-12 08:00:00
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import { connect } from 'react-redux';
import { Banner } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Xmas22Constants } from 'src/constants/campaigns';
import history from 'src/routes/history';
import './bfcm2022.css';
import { allowToShowSpecialDiscount, checkDateCountDown } from 'src/helpers/dashboard-helper';

type Props = {
  name: string,
  welcome: Object
};

type ComposedProps = Props & WithI18nProps;

export const TypeSpecialDiscountBlockNotification = {
  Notification: 'Notification',
  NotificationCountdown: 'NotificationCountdown',
  NotificationCoupon: 'NotificationCoupon',
  None: 'None'
};

const SpecialDiscountBlockNotificationSupport: React.FC<ComposedProps> = ({
  tenantApp,
  i18n,
  type = TypeSpecialDiscountBlockNotification.Notification,
  handleDismissInfoNotification,
  countDownDays
}) => {
  const location = useLocation();

  const countUnit =
    countDownDays > 1
      ? i18n.translate(`AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_days`)
      : i18n.translate(`AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_day`);

  let title;
  switch (type) {
    case TypeSpecialDiscountBlockNotification.NotificationCountdown: {
      title =
        countDownDays > 0
          ? i18n.translate(
              'AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_main_redeem',
              {
                daysLeft: (
                  <b>
                    {countDownDays} {countUnit}
                  </b>
                )
              }
            )
          : `🎁 ${i18n.translate(
              `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Popup_countdown_hours`
            )}`;
      break;
    }
    case TypeSpecialDiscountBlockNotification.NotificationCoupon: {
      title = i18n.translate('AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Coupon_main');
      break;
    }
    default: {
      title = i18n.translate('AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_main');
    }
  }
  return (
    <div className="bfcm2022_banner">
      <Banner
        status="info"
        action={
          location.pathname !== '/plan-discount' && {
            onAction: () => handleSubscribe(tenantApp),
            content: i18n.translate(
              `AdminHTML.Common.ShopifyEmbeddedAppRoute.${
                countDownDays >= 1 ? 'BFCM2022_Notification_btn' : 'applyNow'
              }`
            )
          }
        }
        onDismiss={handleDismissInfoNotification}>
        <div>
          <div style={{ marginBottom: '6px' }}>
            <b style={{ color: 'rgba(212, 76, 71, 1)', fontSize: '16px', lineHeight: '20px' }}>
              {title}
            </b>
            {type === TypeSpecialDiscountBlockNotification.NotificationCoupon && (
              <span>
                {' '}
                {i18n.translate('AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Coupon_sub', {
                  code: (
                    <b>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub1_code`
                      )}
                    </b>
                  ),
                  off: (
                    <b>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub1_off`
                      )}
                    </b>
                  ),
                  duration: (
                    <b>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Coupon_duration`
                      )}
                    </b>
                  )
                })}
              </span>
            )}
          </div>
          {type !== TypeSpecialDiscountBlockNotification.NotificationCoupon && (
            <div>
              {i18n.translate(
                'AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub1',
                {
                  code: (
                    <b>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub1_code`
                      )}
                    </b>
                  ),
                  off: (
                    <b>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub1_off`
                      )}
                    </b>
                  ),
                  date: (
                    <span>
                      {i18n.translate(
                        `AdminHTML.Common.ShopifyEmbeddedAppRoute.BFCM2022_Notification_sub2_date`
                      )}
                    </span>
                  )
                }
              )}
            </div>
          )}
        </div>
      </Banner>
    </div>
  );
};

// Route to plan discount page
const handleSubscribe = tenantApp => {
  try {
    localStorage.removeItem(`${tenantApp.tenantId}-dismissXmas22`);
  } catch (err) {}
  history.push({
    pathname: '/plan-discount',
    search: '?&itm_source=inapp&itm_medium=select-plan&itm_campaign=xmas22'
  });
};

const SpecialDiscountBlockNotification: React.FC<ComposedProps> = ({
  tenantApp,
  themesLoading,
  i18n,
  isAllowSpecialDiscount
}) => {
  const location = useLocation();
  const [typeBlock, setTypeBlock] = useState(TypeSpecialDiscountBlockNotification.Notification);
  const [countDownDays, setCountDownDays] = useState(0);
  const [isShowBlockNotification, setIsShowBlockNotification] = useState(true);

  useEffect(() => {
    if (location.pathname === '/') {
      const countdown = checkDateCountDown(Xmas22Constants.endDate);
      setCountDownDays(countdown);
      if (countdown <= 5 && countdown >= 0) {
        setTypeBlock(TypeSpecialDiscountBlockNotification.NotificationCountdown);
      }
    } else if (location.pathname === '/plan-discount') {
      setTypeBlock(TypeSpecialDiscountBlockNotification.NotificationCoupon);
    } else {
      setTypeBlock(TypeSpecialDiscountBlockNotification.None);
    }
  }, [location.pathname]);

  useEffect(() => {
    try {
      const isDismissXmas22 = localStorage.getItem(`${tenantApp.tenantId}-dismissXmas22`);
      if (isDismissXmas22) {
        setIsShowBlockNotification(false);
      }
    } catch (err) {
      console.log(err);
    }
  }, [tenantApp.tenantId]);

  const handleDismissInfoNotification = () => {
    try {
      localStorage.setItem(`${tenantApp.tenantId}-dismissXmas22`, true);
    } catch (error) {}
    setIsShowBlockNotification(false);
  };

  return isAllowSpecialDiscount && isShowBlockNotification ? (
    <SpecialDiscountBlockNotificationSupport
      type={typeBlock}
      handleDismissInfoNotification={handleDismissInfoNotification}
      tenantApp={tenantApp}
      themesLoading={themesLoading}
      i18n={i18n}
      countDownDays={countDownDays}
    />
  ) : (
    <></>
  );
};

const mapStateToProps = (state, props) => {
  const tenantApp = state.oauth.tenantApp;
  const externalInfo = _.get(tenantApp, 'externalInfo', {});
  const welcome = _.get(state.dashboard, 'dashboardComponents.welcomes.data', {});
  const isAllowSpecialDiscount = allowToShowSpecialDiscount(
    tenantApp,
    Xmas22Constants.startDate,
    Xmas22Constants.endDate
  );

  return {
    name: _.get(externalInfo, 'name', ''),
    welcome,
    isAllowSpecialDiscount,
    tenantApp
  };
};

export default connect(mapStateToProps)(withI18n()(SpecialDiscountBlockNotification));
